import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import PageHeader from "../PageHeader";
import Loader from "../../common/Loader";
import DataTable from "react-data-table-component";
import { triggerAlert } from "../../utils";
import { AdminPhoneVerifications } from "../../utils/APIClient";
import PhoneVerificationDetailDialog from "./PhoneVerificationDetailDialog";

/**
 * Settings > Phone Verifications
 *
 * Compliance review queue for organizations trying to buy US phone
 * numbers. Customer uploads three documents from the customer portal
 * (EIN, Incorporation cert, Card-holder US address proof). A reviewer
 * here approves — unlocking Buy Numbers — or rejects with a reason,
 * which surfaces on the customer's Buy Numbers page and unlocks
 * re-upload.
 *
 * Existing DID-owning orgs must still verify before buying MORE
 * numbers. Their current numbers keep working — this is a soft gate
 * on new purchases only.
 */

const STATUS_TABS = [
    { key: "pending", label: "Pending review" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "unverified", label: "Unverified" },
];

const statusBadge = (s) => {
    if (s === "approved") return <span className="badge bg-success">Approved</span>;
    if (s === "pending") return <span className="badge bg-warning text-dark">Pending</span>;
    if (s === "rejected") return <span className="badge bg-danger">Rejected</span>;
    return <span className="badge bg-secondary">Unverified</span>;
};

const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
};

export default function PhoneVerifications() {
    document.title = "Digital Employees - Phone Verifications";
    const mainHeading = "Phone Verifications";
    const heading = "Settings / Phone Verifications";

    const [statusFilter, setStatusFilter] = useState("pending");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await AdminPhoneVerifications.list({ status: statusFilter });
            const list = resp.data?.results || [];
            list.forEach((item, i) => (item.Num = i + 1));
            setRows(list);
        } catch (e) {
            setRows([]);
            triggerAlert("error", "Oops...", "Failed to load phone-verification queue.");
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        fetchRows();
    }, [fetchRows]);

    const columns = useMemo(
        () => [
            {
                name: "#",
                selector: (r) => r.Num,
                width: "70px",
            },
            {
                name: "Organization",
                selector: (r) => r.org_name || "—",
                sortable: true,
                grow: 2,
                cell: (r) => (
                    <div>
                        <div className="fw-semibold">{r.org_name || "—"}</div>
                        <div className="text-muted small">{r.org_id}</div>
                    </div>
                ),
            },
            {
                name: "Status",
                width: "130px",
                cell: (r) => statusBadge(r.status),
            },
            {
                name: "Submitted",
                selector: (r) => r.submitted_at || "",
                sortable: true,
                cell: (r) => fmtDateTime(r.submitted_at),
            },
            {
                name: "Reviewed",
                selector: (r) => r.reviewed_at || "",
                sortable: true,
                cell: (r) => (
                    <div>
                        <div>{fmtDateTime(r.reviewed_at)}</div>
                        {r.reviewed_by && (
                            <div className="text-muted small">by {r.reviewed_by}</div>
                        )}
                    </div>
                ),
            },
            {
                name: "Actions",
                width: "150px",
                cell: (r) => (
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setDetail(r)}
                    >
                        View / Review
                    </button>
                ),
            },
        ],
        [],
    );

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: "#f8f9fa",
                fontWeight: 600,
                borderTop: "1px solid #dee2e6",
                borderBottom: "1px solid #dee2e6",
            },
        },
        pagination: { style: { borderTop: "1px solid #dee2e6", padding: "8px" } },
    };

    return (
        <>
            <div id="layout-wrapper">
                <Header />
                <Sidebar />

                <div className="main-content">
                    <div className="page-content" style={{ backgroundColor: "#ededed" }}>
                        <div className="container-fluid">
                            <PageHeader mainHeading={mainHeading} heading={heading} />

                            <div className="row">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-body">
                                            <ul className="nav nav-tabs mb-3">
                                                {STATUS_TABS.map((t) => (
                                                    <li className="nav-item" key={t.key}>
                                                        <button
                                                            className={
                                                                "nav-link" +
                                                                (statusFilter === t.key ? " active" : "")
                                                            }
                                                            onClick={() => setStatusFilter(t.key)}
                                                        >
                                                            {t.label}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>

                                            <div className="alert alert-info py-2 mb-3" role="alert">
                                                <strong>Reviewer checklist:</strong> confirm each doc
                                                matches the organization name, EIN is legible, the
                                                incorporation certificate is state-issued, and the
                                                address proof is dated within the last 90 days.
                                                Rejection reasons are shown to the customer verbatim.
                                            </div>

                                            {!loading ? (
                                                <DataTable
                                                    columns={columns}
                                                    data={rows}
                                                    pagination
                                                    fixedHeader
                                                    fixedHeaderScrollHeight="600px"
                                                    persistTableHead
                                                    customStyles={customStyles}
                                                    noDataComponent={
                                                        <div className="p-4 text-muted">
                                                            No organizations match this filter.
                                                        </div>
                                                    }
                                                />
                                            ) : (
                                                <div className="loader-overlay text-white">
                                                    <Loader />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </div>

            {detail && (
                <PhoneVerificationDetailDialog
                    row={detail}
                    onClose={() => setDetail(null)}
                    onDecision={() => {
                        setDetail(null);
                        fetchRows();
                    }}
                />
            )}
        </>
    );
}
